import React, { useEffect, useState } from 'react';
import { Input } from './Input';
import { LoginType } from '../models/LoginType';
import { authRepository, OtpReason } from '../repositories/AuthRepository';
import { AxiosError } from 'axios';
import { InvalidOtp } from '../repositories/Repository';
import OtpInput from 'react-otp-input';
import { useConfirm } from '../hooks/useConfirm';
import { Credentials } from '../models/Credentials';
import { Pleb } from '../models/Pleb';
import { ButtonVariant } from '../config/colors';

interface InputOtpProps {
    loginType: LoginType;
    email?: string;
    npub?: string;
    otpId: string;
    onVerify: (credentials: Credentials, pleb: Pleb) => void;
    otpReason: OtpReason;
}

const InputOtp = ({ loginType, email, npub, otpId, onVerify, otpReason }: InputOtpProps) => {
    const [currentOtpId, setCurrentOtpId] = useState('');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { openConfirm } = useConfirm();

    useEffect(() => {
        setCurrentOtpId(otpId);
    }, [otpId]);
    
    const emailOrNpub = loginType === LoginType.EMAIL 
    ? email 
    : `${npub?.substring(0, 7)}......${npub?.substring(npub?.length - 7)}`;

    const handleOtpChange = (value: string) => {
        setOtp(value);
        if (value.length === 4 && !isLoading) {
            handleComplete(value);
        }
    };

  const handleComplete = async (value: string) => {
    setIsLoading(true);
    try {
      const { credentials, pleb } = await authRepository.verifyOtp({ otpId: currentOtpId, otp: value });
      onVerify(credentials, pleb);

    } catch (err) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.message || 'Verification failed');
      }
      else if(err instanceof InvalidOtp){
        openConfirm({
            message: "Invalid code",
            buttons: [
                { 
                    label: "Get a new code", 
                    variant: ButtonVariant.transparent,
                    onClick: () => {
                        if(loginType)
                        {
                            const identifier = loginType === LoginType.EMAIL ? email : npub;
                            if(identifier)
                            {
                                resetOtpInput();
                                return authRepository.sendOtp(loginType, identifier, otpReason)
                                    .then((otpId) => {
                                        setCurrentOtpId(otpId);
                                    });
                            }
                        }
                    }
                }
            ]
        });
      }
      console.error(err);
    }
    finally {
      setIsLoading(false);
    }
  };

    const resetOtpInput = () => {
        setOtp('');
    };
      
    return (
        <div className="flex flex-col items-center justify-center p-5">
      <h1 className="text-4xl font-bold text-white mb-6">
        Verify
      </h1>
      
      <p className="text-lg text-white text-center mb-4">
        Enter the 4 digit code sent to:
        <br />
        {emailOrNpub}
      </p>
      <div className="my-4">
        <OtpInput
          value={otp}
          onChange={handleOtpChange}
          numInputs={4}
          shouldAutoFocus={true}
          renderInput={(props) => <Input {...props} />}
          containerStyle="flex justify-center items-center gap-6"
          inputStyle={{
            width: '50px',
            height: '50px',
            padding: '0 10px'
          }}
          inputType="number"
        />
      </div>

      <p className="text-sm text-white/80 text-center mt-4">
        {loginType === LoginType.EMAIL 
          ? 'No code? Check your spam folder'
          : 'No code? Check your Other folder'
        }
      </p>

      {error && (
        <p className="text-red-500 mt-2 text-center">
          {error}
        </p>
      )}
    </div>
    )
}

export default InputOtp;